import { NodeProps } from '@xyflow/react'
import { GhostNode } from 'features/processFlowDiagrams/components/nodes/GhostNode'
import {
  UniversalSymbolNode,
  UniversalSymbolPreview,
} from 'features/processFlowDiagrams/components/nodes/UniversalSymbolNode'
import React from 'react'
import { PfdNode } from 'types'

import {
  getAllCategories,
  getSymbolsByCategory as getMinimalSymbolsByCategory,
  MinimalSymbol,
} from './MinimalSymbols'

// Convert minimal symbols to library format
export interface SymbolDefinition {
  id: string
  name: string
  description?: string
  category: string
  tags: string[]
  component: React.ComponentType<NodeProps<PfdNode>>
  previewComponent: React.ComponentType
}

export interface SymbolCategory {
  id: string
  name: string
  description?: string
  symbols: SymbolDefinition[]
}

// Convert minimal symbol to full symbol definition
const convertMinimalSymbol = (
  minimalSymbol: MinimalSymbol
): SymbolDefinition => ({
  id: minimalSymbol.id,
  name: minimalSymbol.name,
  description: `${minimalSymbol.name} symbol`,
  category: minimalSymbol.category,
  tags: minimalSymbol.tags || [],
  component: (props: NodeProps<PfdNode>) => (
    <UniversalSymbolNode {...props} symbolId={minimalSymbol.id} />
  ),
  previewComponent: () => (
    <UniversalSymbolPreview symbolId={minimalSymbol.id} />
  ),
})

// Category metadata
const CATEGORY_INFO = {
  basic: { name: 'Basic Shapes', description: 'Basic geometric shapes' },
  instruments: {
    name: 'Instruments',
    description: 'Measurement and control instruments',
  },
  valves: { name: 'Valves', description: 'Flow control valves' },
  pumps: { name: 'Pumps', description: 'Fluid pumping equipment' },
  dryers: { name: 'Dryers', description: 'Drying equipment' },
  agitators: {
    name: 'Agitators',
    description: 'Mixers and agitator elements',
  },
  'apparatus-elements': {
    name: 'Apparatus Elements',
    description: 'Apparatus components',
  },
  'crushers-grinding': {
    name: 'Crushers Grinding',
    description: 'Crushers and grinders',
  },
  vessels: {
    name: 'Vessels',
    description: 'Storage tanks and pressure vessels',
  },
  compressors: {
    name: 'Compressors',
    description: 'Gas compression equipment',
  },
  misc: {
    name: 'Misc.',
    description: 'Miscellaneous P&ID shapes',
  },
  centrifuges: {
    name: 'Centrifuges',
    description: 'Centrifugal separation equipment',
  },
  'compressors-iso': {
    name: 'Compressors ISO',
    description: 'ISO standard compressor symbols',
  },
  engines: {
    name: 'Engines',
    description: 'Motors and engine equipment',
  },
  feeders: {
    name: 'Feeders',
    description: 'Material feeding equipment',
  },
  filters: {
    name: 'Filters',
    description: 'Filtration equipment',
  },
  fittings: {
    name: 'Fittings',
    description: 'Pipe fittings and connections',
  },
  'flow-sensors': {
    name: 'Flow Sensors',
    description: 'Flow measurement instruments',
  },
  'heat-exchangers': {
    name: 'Heat Exchangers',
    description: 'Heat transfer equipment',
  },
  mixers: {
    name: 'Mixers',
    description: 'Mixing and blending equipment',
  },
  piping: {
    name: 'Piping',
    description: 'Piping and pipeline components',
  },
  'pumps-din': {
    name: 'Pumps DIN',
    description: 'DIN standard pump symbols',
  },
  'pumps-iso': {
    name: 'Pumps ISO',
    description: 'ISO standard pump symbols',
  },
  separators: {
    name: 'Separators',
    description: 'Separation and purification equipment',
  },
  'shaping-machines': {
    name: 'Shaping Machines',
    description: 'Material shaping and forming machines',
  },
}

// Generate symbol categories from minimal symbols
export const SYMBOL_CATEGORIES: SymbolCategory[] = getAllCategories().map(
  (categoryId) => {
    const categorySymbols = getMinimalSymbolsByCategory(categoryId)
    const categoryInfo = CATEGORY_INFO[
      categoryId as keyof typeof CATEGORY_INFO
    ] || { name: categoryId, description: `${categoryId} equipment` }

    return {
      id: categoryId,
      name: categoryInfo.name,
      description: categoryInfo.description,
      symbols: categorySymbols.map(convertMinimalSymbol),
    }
  }
)

// Allow runtime registration of custom symbols (e.g., from Symbol Import UI)
const RUNTIME_SYMBOLS: SymbolDefinition[] = []
const RUNTIME_CATEGORY_ID = 'custom'

export function registerCustomSymbol(def: SymbolDefinition) {
  // Replace if id exists
  const existingIdx = RUNTIME_SYMBOLS.findIndex((s) => s.id === def.id)
  if (existingIdx >= 0) {
    RUNTIME_SYMBOLS.splice(existingIdx, 1, def)
  } else {
    RUNTIME_SYMBOLS.push(def)
  }
}

// Helper functions (compatible with existing symbol library interface)
export const getAllSymbols = (): SymbolDefinition[] => {
  return [
    ...SYMBOL_CATEGORIES.flatMap((category) => category.symbols),
    ...RUNTIME_SYMBOLS,
  ]
}

export const getSymbolById = (id: string): SymbolDefinition | undefined => {
  return getAllSymbols().find((symbol) => symbol.id === id)
}

export const getSymbolsByCategory = (
  categoryId: string
): SymbolDefinition[] => {
  if (categoryId === RUNTIME_CATEGORY_ID) return RUNTIME_SYMBOLS
  const category = SYMBOL_CATEGORIES.find((cat) => cat.id === categoryId)
  return category?.symbols || []
}

export const searchSymbols = (query: string): SymbolDefinition[] => {
  if (!query.trim()) return getAllSymbols()

  const lowerQuery = query.toLowerCase()
  return getAllSymbols().filter(
    (symbol) =>
      symbol.name.toLowerCase().includes(lowerQuery) ||
      symbol.description?.toLowerCase().includes(lowerQuery)
  )
}

// Create a mapping for ReactFlow nodeTypes
export const createNodeTypesMapping = () => {
  const nodeTypes: Record<string, React.ComponentType<NodeProps<PfdNode>>> = {}

  getAllSymbols().forEach((symbol) => {
    nodeTypes[symbol.id] = symbol.component
  })

  // Ensure common legacy ids exist even if not present in the generated sets
  if (!nodeTypes['filter']) {
    const candidate =
      getAllSymbols().find((s) => s.id === 'filter') ||
      getAllSymbols().find((s) => s.name.toLowerCase().includes('filter'))
    if (candidate) nodeTypes['filter'] = candidate.component
  }

  nodeTypes['ghost'] = GhostNode
  return nodeTypes
}
